import { useQuery } from '@tanstack/react-query';
import { getSlidesTileGenerationStatus } from 'api/slides';
import { chunk, keyBy, uniq } from 'es-toolkit';
import { JobStatus } from 'interfaces/job';
import { SlideTileGenerationStatus } from 'interfaces/tileGeneration';

const ACTIVE_TILE_GENERATION_REFRESH_MS = 10000;
// Matches MAX_TILE_GENERATION_STATUS_SLIDES in the backend request schema.
const MAX_SLIDES_PER_REQUEST = 200;

export const isTileGenerationActive = (status?: string | null) =>
  status === JobStatus.Pending || status === JobStatus.Running;

export const SLIDES_TILE_GENERATION_STATUS_QUERY_KEY = 'slidesTileGenerationStatus';

export const useSlidesTileGenerationStatus = ({
  labId,
  studyId,
  slideIds,
  enabled = true,
}: {
  labId?: string;
  studyId?: string;
  slideIds: string[];
  enabled?: boolean;
}) => {
  const requestedSlideIds = uniq(slideIds).sort();
  return useQuery({
    queryKey: [SLIDES_TILE_GENERATION_STATUS_QUERY_KEY, labId, studyId, requestedSlideIds],
    queryFn: async ({ signal }): Promise<Record<string, SlideTileGenerationStatus>> => {
      const responses = await Promise.all(
        chunk(requestedSlideIds, MAX_SLIDES_PER_REQUEST).map((slideIdsChunk) =>
          getSlidesTileGenerationStatus({ labId: labId!, studyId: studyId!, slideIds: slideIdsChunk }, signal)
        )
      );
      return keyBy(
        responses.flatMap((response) => response.slides),
        (status) => status.slideId
      );
    },
    enabled: enabled && Boolean(labId) && Boolean(studyId) && requestedSlideIds.length > 0,
    // The app default never goes stale, which would keep a settled status forever.
    staleTime: ACTIVE_TILE_GENERATION_REFRESH_MS,
    refetchInterval: (statusesBySlideId) =>
      Object.values(statusesBySlideId ?? {}).some((status) => isTileGenerationActive(status.status))
        ? ACTIVE_TILE_GENERATION_REFRESH_MS
        : false,
  });
};
